import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, LayoutGrid, Calendar, Layers, Share2, Download, Presentation, ListTodo, Activity, X, Filter, Save, Trash2, ChevronDown, ShieldAlert } from 'lucide-react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import api from '../api';
import TimelineView from './TimelineView';
import NowNextLaterView from './NowNextLaterView';
import QuarterlyView from './QuarterlyView';
import KanbanView from './KanbanView';
import ItemDrawer from './ItemDrawer';
import SyncPill from './SyncPill';
import ActivityFeed from './ActivityFeed';
import { Breadcrumbs } from './Breadcrumbs';
import { AIRiskScanner } from './AIRiskScanner';

const VIEWS = [
  { key: 'now-next-later', label: 'Now-Next-Later', icon: Layers },
  { key: 'kanban', label: 'Kanban', icon: ListTodo },
  { key: 'timeline', label: 'Timeline', icon: Calendar },
  { key: 'quarterly', label: 'Quarterly', icon: LayoutGrid },
];

function RoadmapView({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [roadmap, setRoadmap] = useState(null);
  const [items, setItems] = useState([]);
  const [metadata, setMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('now-next-later');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showItemDrawer, setShowItemDrawer] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showActivityFeed, setShowActivityFeed] = useState(false);
  const [showRiskPanel, setShowRiskPanel] = useState(false);

  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: '', priority: '', type: '', owner: '' });
  const [savedViews, setSavedViews] = useState([]);
  const [showSaveView, setShowSaveView] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');

  useEffect(() => { loadData(); }, [id]);

  const loadData = async () => {
    try {
      const [roadmapData, itemsData, statusMetadata, views] = await Promise.all([
        api.getRoadmap(id), api.getItems(id), api.getMetadata('status'), api.getSavedViews(id).catch(() => [])
      ]);
      setRoadmap(roadmapData); setItems(itemsData); setMetadata(statusMetadata); setSavedViews(views);
    } catch (error) { console.error('Failed to load data:', error); }
    finally { setLoading(false); }
  };

  // Filter items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      if (filters.status && item.status !== filters.status) return false;
      if (filters.priority && item.priority !== filters.priority) return false;
      if (filters.type && item.type !== filters.type) return false;
      if (filters.owner && !(item.owner || '').toLowerCase().includes(filters.owner.toLowerCase())) return false;
      return true;
    });
  }, [items, filters]);

  const activeFilterCount = Object.values(filters).filter(v => v).length;
  const uniqueOwners = [...new Set(items.map(i => i.owner).filter(Boolean))];

  const handleItemClick = (item) => { setSelectedItem(item); setShowItemDrawer(true); };

  const handleItemUpdate = async (updatedItem) => {
    try {
      const updated = await api.updateItem(updatedItem._id, updatedItem);
      setItems(items.map((i) => (i._id === updated._id ? updated : i)));
      setSelectedItem(updated);
    } catch (error) { console.error('Failed to update item:', error); }
  };

  const handleItemDelete = async (itemId) => {
    try { await api.deleteItem(itemId); setItems(items.filter((i) => i._id !== itemId)); setShowItemDrawer(false); setSelectedItem(null); }
    catch (error) { console.error('Failed to delete item:', error); }
  };

  const handleCreateItem = async (newItem) => {
    try { const created = await api.createItem({ ...newItem, roadmap_id: id }); setItems([...items, created]); setShowCreateModal(false); }
    catch (error) { console.error('Failed to create item:', error); }
  };

  const handleShare = async () => {
    try {
      const shareLink = await api.createShareLink(id, { expires_days: 30 });
      const url = `${window.location.origin}/share/${shareLink.token}`;
      await navigator.clipboard.writeText(url);
      alert('Share link copied to clipboard!');
    } catch (error) { console.error('Failed to create share link:', error); }
  };

  const handleExport = () => {
    const csv = [
      ['Title', 'Type', 'Status', 'Priority', 'Timeframe', 'Owner'].join(','),
      ...items.map((item) => [item.title, item.type, item.status, item.priority, item.timeframe, item.owner || ''].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = `${roadmap?.name || 'roadmap'}.csv`; a.click();
  };

  const handleSaveView = async () => {
    if (!saveViewName.trim()) return;
    try {
      const view = await api.createSavedView(id, { name: saveViewName, view_type: activeView, filters });
      setSavedViews([...savedViews, view]);
      setShowSaveView(false); setSaveViewName('');
    } catch (e) { console.error(e); }
  };

  const handleLoadView = (view) => {
    setActiveView(view.view_type);
    setFilters(view.filters || { status: '', priority: '', type: '', owner: '' });
  };

  const handleDeleteView = async (viewId) => {
    try { await api.deleteSavedView(viewId); setSavedViews(savedViews.filter(v => v._id !== viewId)); } catch (e) { console.error(e); }
  };

  const clearFilters = () => setFilters({ status: '', priority: '', type: '', owner: '' });

  if (loading) return (
    <div className="flex items-center justify-center h-[60vh]">
      <div className="animate-spin rounded-full h-10 w-10 border-2 border-muted border-t-primary"></div>
    </div>
  );

  return (
    <div className="min-h-full" data-testid="roadmap-view-page">
      {/* Header */}
      <div className="border-b border-border bg-card/50 backdrop-blur-md sticky top-0 z-30">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="mb-3">
            <Breadcrumbs items={[
              { label: 'Dashboard', path: '/' },
              { label: roadmap?.name || 'Roadmap', path: `/roadmap/${id}` },
            ]} />
          </div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold tracking-tight" data-testid="roadmap-title">{roadmap?.name}</h1>
              <p className="text-sm text-muted-foreground hidden sm:block">{roadmap?.description}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowRiskPanel(!showRiskPanel)}
                className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${showRiskPanel ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent'}`}
                data-testid="risk-scan-button">
                <ShieldAlert className="h-4 w-4" /> <span className="hidden sm:inline">AI Risks</span>
              </button>
              <button onClick={() => setShowActivityFeed(!showActivityFeed)}
                className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-colors ${showActivityFeed ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent'}`}
                data-testid="activity-button">
                <Activity className="h-4 w-4" /> <span className="hidden sm:inline">Activity</span>
              </button>
              <button onClick={() => navigate(`/roadmap/${id}/present`)} className="px-3 py-2 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium flex items-center gap-2 border border-transparent transition-colors hidden sm:flex" data-testid="present-button">
                <Presentation className="h-4 w-4" /> Present
              </button>
              <button onClick={handleShare} className="px-3 py-2 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium flex items-center gap-2 border border-transparent transition-colors hidden lg:flex" data-testid="share-button">
                <Share2 className="h-4 w-4" /> Share
              </button>
              <button onClick={handleExport} className="px-3 py-2 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium flex items-center gap-2 border border-transparent transition-colors hidden lg:flex" data-testid="export-button">
                <Download className="h-4 w-4" /> Export
              </button>
              <button onClick={() => setShowCreateModal(true)} className="px-4 py-2 bg-primary text-primary-foreground rounded-xl hover:opacity-90 text-sm font-semibold flex items-center gap-2 shadow-sm shadow-primary/20 btn-press transition-opacity" data-testid="create-item-button">
                <Plus className="h-4 w-4" /> <span className="hidden sm:inline">New Item</span>
              </button>
            </div>
          </div>

          {/* View switcher + filter toggle */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex gap-1 bg-muted/50 p-1 rounded-xl w-fit" data-testid="roadmap-view-switcher-tabs">
              {VIEWS.map((view) => (
                <button key={view.key} onClick={() => setActiveView(view.key)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors ${activeView === view.key ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
                  data-testid={`${view.key}-view-button`}>
                  <view.icon className="h-4 w-4" />
                  <span className="hidden sm:inline">{view.label}</span>
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {/* Saved views dropdown */}
              {savedViews.length > 0 && (
                <div className="relative group">
                  <button className="px-3 py-2 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 text-sm font-medium flex items-center gap-1.5 border border-transparent transition-colors" data-testid="saved-views-trigger">
                    <Save className="h-3.5 w-3.5" /> Views <ChevronDown className="h-3.5 w-3.5" />
                  </button>
                  <div className="absolute right-0 top-full mt-1 w-56 bg-card border border-border rounded-xl shadow-xl hidden group-hover:block z-20" data-testid="saved-views-dropdown">
                    {savedViews.map(v => (
                      <div key={v._id} className="flex items-center justify-between px-3 py-2 hover:bg-muted/50 transition-colors">
                        <button onClick={() => handleLoadView(v)} className="text-sm text-left flex-1 truncate">{v.name}</button>
                        <button onClick={() => handleDeleteView(v._id)} className="p-1 text-muted-foreground hover:text-destructive transition-colors"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setShowFilters(!showFilters)}
                className={`px-3 py-2 rounded-xl text-sm font-medium flex items-center gap-1.5 transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-secondary text-secondary-foreground hover:bg-secondary/80 border border-transparent'}`}
                data-testid="filter-toggle">
                <Filter className="h-3.5 w-3.5" /> Filter
                {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">{activeFilterCount}</span>}
              </button>
            </div>
          </div>

          {/* Filter bar */}
          {showFilters && (
            <div className="mt-3 p-3 bg-muted/30 rounded-xl border border-border flex flex-wrap gap-3 items-end" data-testid="filter-bar">
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Status</label>
                <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className="px-3 py-1.5 border border-input rounded-lg bg-background text-xs" data-testid="filter-status">
                  <option value="">All</option>
                  <option value="planned">Planned</option><option value="in-progress">In Progress</option><option value="done">Done</option><option value="on-hold">On Hold</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Priority</label>
                <select value={filters.priority} onChange={(e) => setFilters({ ...filters, priority: e.target.value })} className="px-3 py-1.5 border border-input rounded-lg bg-background text-xs" data-testid="filter-priority">
                  <option value="">All</option>
                  <option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Type</label>
                <select value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} className="px-3 py-1.5 border border-input rounded-lg bg-background text-xs" data-testid="filter-type">
                  <option value="">All</option>
                  <option value="initiative">Initiative</option><option value="epic">Epic</option><option value="feature">Feature</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-1">Owner</label>
                <select value={filters.owner} onChange={(e) => setFilters({ ...filters, owner: e.target.value })} className="px-3 py-1.5 border border-input rounded-lg bg-background text-xs" data-testid="filter-owner">
                  <option value="">All</option>
                  {uniqueOwners.map(o => <option key={o} value={o}>{o.split('@')[0]}</option>)}
                </select>
              </div>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="px-3 py-1.5 text-xs text-destructive hover:bg-destructive/10 rounded-lg transition-colors" data-testid="clear-filters">Clear</button>
              )}
              <div className="ml-auto flex items-center gap-2">
                {showSaveView ? (
                  <div className="flex gap-1">
                    <input type="text" value={saveViewName} onChange={(e) => setSaveViewName(e.target.value)} placeholder="View name" className="px-2 py-1.5 border border-input rounded-lg bg-background text-xs w-32" data-testid="save-view-name" />
                    <button onClick={handleSaveView} className="px-3 py-1.5 bg-primary text-primary-foreground rounded-lg text-xs font-semibold" data-testid="save-view-confirm">Save</button>
                    <button onClick={() => setShowSaveView(false)} className="px-2 py-1.5 text-xs text-muted-foreground">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setShowSaveView(true)} className="px-3 py-1.5 text-xs text-primary hover:bg-primary/10 rounded-lg transition-colors flex items-center gap-1" data-testid="save-view-trigger">
                    <Save className="h-3 w-3" /> Save View
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Active filter count */}
          {activeFilterCount > 0 && !showFilters && (
            <div className="mt-2 text-xs text-muted-foreground">
              Showing {filteredItems.length} of {items.length} items
              <button onClick={clearFilters} className="ml-2 text-primary hover:underline">Clear filters</button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        <DndProvider backend={HTML5Backend}>
          <div className="flex gap-6">
            <div className={(showActivityFeed || showRiskPanel) ? 'flex-1 min-w-0' : 'w-full'}>
              {activeView === 'now-next-later' && <NowNextLaterView items={filteredItems} onItemClick={handleItemClick} onItemUpdate={handleItemUpdate} />}
              {activeView === 'kanban' && <KanbanView items={filteredItems} metadata={metadata} onItemClick={handleItemClick} onItemUpdate={handleItemUpdate} />}
              {activeView === 'timeline' && <TimelineView items={filteredItems} onItemClick={handleItemClick} onItemUpdate={handleItemUpdate} />}
              {activeView === 'quarterly' && <QuarterlyView items={filteredItems} onItemClick={handleItemClick} onItemUpdate={handleItemUpdate} />}
            </div>
            {showRiskPanel && (
              <div className="hidden lg:block w-[380px] shrink-0" data-testid="risk-panel">
                <div className="sticky top-[200px]">
                  <AIRiskScanner roadmapId={id} />
                </div>
              </div>
            )}
            {showActivityFeed && !showRiskPanel && (
              <div className="hidden lg:block w-[340px] shrink-0">
                <div className="bg-card border border-border rounded-xl p-4 sticky top-[200px]">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold">Activity Feed</h3>
                    <button onClick={() => setShowActivityFeed(false)} className="p-1 hover:bg-secondary rounded-md transition-colors"><X className="h-4 w-4 text-muted-foreground" /></button>
                  </div>
                  <ActivityFeed roadmapId={id} />
                </div>
              </div>
            )}
          </div>
        </DndProvider>
      </div>

      {showItemDrawer && selectedItem && <ItemDrawer item={selectedItem} onClose={() => setShowItemDrawer(false)} onUpdate={handleItemUpdate} onDelete={handleItemDelete} allItems={items} roadmapId={id} />}
      {showCreateModal && <CreateItemModal onClose={() => setShowCreateModal(false)} onCreate={handleCreateItem} />}
      <SyncPill />
    </div>
  );
}

function CreateItemModal({ onClose, onCreate }) {
  const [formData, setFormData] = useState({ title: '', description: '', type: 'feature', status: 'planned', priority: 'medium', timeframe: 'now', owner: '', start_date: '', end_date: '', tags: [] });
  const handleSubmit = (e) => { e.preventDefault(); onCreate(formData); };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="create-item-modal">
      <div className="bg-card rounded-xl max-w-2xl w-full border border-border shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-bold">Create New Item</h2>
          <p className="text-sm text-muted-foreground mt-1">Add a new item to your roadmap</p>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Title *</label>
            <input type="text" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Enter item title" data-testid="item-title-input" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={3} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" placeholder="Describe this item" data-testid="item-description-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Type</label>
              <select value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="item-type-select">
                <option value="initiative">Initiative</option><option value="epic">Epic</option><option value="feature">Feature</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Status</label>
              <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="item-status-select">
                <option value="planned">Planned</option><option value="in-progress">In Progress</option><option value="done">Done</option><option value="on-hold">On Hold</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="item-priority-select">
                <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Timeframe</label>
              <select value={formData.timeframe} onChange={(e) => setFormData({ ...formData, timeframe: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="item-timeframe-select">
                <option value="now">Now</option><option value="next">Next</option><option value="later">Later</option><option value="Q1">Q1</option><option value="Q2">Q2</option><option value="Q3">Q3</option><option value="Q4">Q4</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Start Date</label>
              <input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="item-start-date" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">End Date</label>
              <input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} className="w-full px-4 py-2.5 border border-input rounded-xl bg-background focus:outline-none focus:ring-2 focus:ring-ring" data-testid="item-end-date" />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2.5 bg-secondary text-secondary-foreground rounded-xl hover:bg-secondary/80 font-medium transition-colors" data-testid="cancel-create-button">Cancel</button>
            <button type="submit" className="px-5 py-2.5 bg-primary text-primary-foreground rounded-xl hover:opacity-90 font-semibold shadow-sm shadow-primary/20 btn-press transition-opacity" data-testid="submit-create-button">Create Item</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RoadmapView;
